import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

import { AuditMessageDto } from '@app/data/dtos/audit/audit.dto';
import { mapAuditMessage } from '@app/data/mappers/audit/audit.mapper';
import { AUDIT_REPOSITORY, AuditRepository } from '@app/domain/repositories/audit/audit.repository.contract';
import { AuditMessage } from '@app/entities/models/audit/audit.model';
import { Logger, NETWORK_USE_CASES } from '@app/services';
import { StorageService, STORAGE_CONSTANTS } from '@app/services/storage';
import { appVersion } from '@app/shared/utils/utils';

import { AuditUseCases } from './audit.use-cases.contract';
import { NetworkUseCases } from '../network/network.use-cases.contract';

import { PAGES } from '@/enums/pages.enum';

@Injectable({
  providedIn: 'root'
})
export class AuditUseCasesImpl implements AuditUseCases {
  #auditRepository: AuditRepository = inject(AUDIT_REPOSITORY);

  version: string = appVersion;
  private logger: Logger = new Logger('AuditService');
  private sendingMessages = false;

  private network: NetworkUseCases = inject(NETWORK_USE_CASES);

  constructor(
    private storage: StorageService,
    public router: Router
  ) {}

  get activePage() {
    return this.router.url.replace('/', '');
  }

  async flush() {
    if (this.activePage === PAGES.MAINTENANCE || this.sendingMessages) {
      return;
    }
    try {
      const auditMessages: AuditMessage[] = (await this.storage.get(STORAGE_CONSTANTS.AUDIT_MESSAGES)) || [];
      if (auditMessages.length > 0) {
        this.sendingMessages = true;
        this.#auditRepository
          .flush(auditMessages)
          .pipe(finalize(() => (this.sendingMessages = false)))
          .subscribe(() => {
            this.setAuditMessages([]);
          });
      }
    } catch (err) {
      this.logger.error('Error on Http request:', err);
      this.sendingMessages = false;
    }
  }

  async push(auditMessageDto: AuditMessageDto, realTime = true) {
    const auditMessage = mapAuditMessage(auditMessageDto);
    this.setVersion(auditMessage);
    if (this.activePage === PAGES.MAINTENANCE) {
      return;
    }
    try {
      const auditMessages: AuditMessage[] = (await this.storage.get(STORAGE_CONSTANTS.AUDIT_MESSAGES)) || [];
      auditMessages.push(auditMessage);
      this.logger.debug('Storage audit messages:');
      await this.setAuditMessages(auditMessages);
      this.logger.debug('Stored messages for next flush:', auditMessages);
      if ((auditMessages.length >= 50 || this.network.hasConnection()) && realTime && !this.sendingMessages) {
        await this.flush();
      }
    } catch (err) {
      this.logger.error('Cannot push audit message in storage:', err);
    }
  }

  private setAuditMessages(auditMessages: AuditMessage[]) {
    return this.storage.set(STORAGE_CONSTANTS.AUDIT_MESSAGES, auditMessages);
  }

  private setVersion(auditMessage: AuditMessage) {
    auditMessage.app_version = this.version;
    return auditMessage;
  }
}
